import { useState } from 'react';
import Button from 'src/components/buttons/button';
import BulletedList from 'src/components/bulletedList';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}lower-your-cholesterol/`;

const getMenu = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li><div className="navigator__contents__number">1.</div><div className="navigator__contents__content-text"><a href="#1">{`Why doesn't everyone with high cholesterol take statins?`}</a></div></li>
      <li><div className="navigator__contents__number">2.</div><div className="navigator__contents__content-text"><a href="#2">How do statins work?</a></div></li>
      <li><div className="navigator__contents__number">3.</div><div className="navigator__contents__content-text"><a href="#3">Statins: common questions answered</a></div></li>
    </ul>
  </section>
);

const getPage = () => (
  <div className="navigator">
    <section className="navigator__content">
      <h1 className="navigator__title">
        {'Week 4: Understanding statins'}
      </h1>
      <p>{`Some people with high cholesterol are prescribed statins, but not everybody will be. That's because your doctor will consider many different factors before prescribing them. Statins can make a big difference to the people who need them.`}</p>
      <div className="visible-xs">{getMenu()}</div>

      <h2>{`Why doesn't everyone with high cholesterol take statins?`}</h2>
      <p>{`You may speak to somebody who has similar cholesterol levels to you, but they have been prescribed different treatment to you. That doesn't mean that one is right and one is wrong; it's about what's best for you.`}</p>
      <p>{`Factors like your age, medical history, your other risk factors and your goal cholesterol levels will contribute to whether or not you're prescribed statins.`}</p>
      <img alt="High and low risk individuals with high cholesterol" className="navigator__image-full" src={`${PROGRAM_URL}W04B01.jpg`} />

      <h2>How do statins work?</h2>
      <p>
        Statins lower your risk of a heart attack or stroke by reducing your cholesterol levels.
        Statins are one of the most well researched drugs in the UK, with significant funding from the BHF for many of the larger trials.
        We explain how they can help you lower your cholesterol and what you need to know about taking them.
      </p>
      <div className="navigator__video-container">
        <iframe
          allow="autoplay; encrypted-media; fullscreen"
          src="https://www.youtube.com/embed/koyo1hpDwW8"
        />
      </div>

      <h2>Statins: common questions answered</h2>
      <section className="navigator__section-half">
        <p>{`It's normal to have questions about a new medication you've been recommended. We put some questions to Professor Richard Hobbs, Head of Primary Care at the University of Oxford, to get his advice.`}</p>
      </section>
      <img alt="Richard Hobbs" className="navigator__image-half" src={`${PROGRAM_URL}W04B03.jpg`} />
      <BulletedList items={[
        <span key="1"><strong>Are statins safe?</strong>{` Yes. We have lots of reliable data - some of which originated from work that's been funded by the BHF - that show they are very safe and effective to take.`}</span>,
        <span key="2"><strong>What are the different types of statins?</strong> Statins can be split into two groups: low-intensity statins and high-intensity statins. The main difference is how much they lower your cholesterol.</span>,
        <span key="3"><strong>What about side effects?</strong> The majority of people can take statins with no side effects. The most common side effect, though still rare, is muscle aches and pains. If this happens, do not stop taking your statins. Instead go to your doctor - they should be able to offer you a different statin that works better for you.</span>,
        <span key="4"><strong>Will I be more likely to become diabetic?</strong> Recent research suggests that statins can increase the risk of people who are already at very high risk of getting Type 2 diabetes. However, it does not mean that people should stop taking their statins. Continue as normal and speak to your GP if you have concerns.</span>]
      }
      />
      <a href="https://www.bhf.org.uk/heart-matters-magazine/medical/drug-cabinet/statins" rel="noreferrer" target="_blank">
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Read more about statins"
        />
      </a>

    </section>
  </div>
);

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="HealthUnlocked British Heart Foundation community screenshot" className="navigator__footer__image" src={`${PROGRAM_URL}cta-community.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">
          Join the conversation on HealthUnlocked
        </div>
        <div className="navigator__footer__description">
          {`To get the most out of your heart health journey, you'll need support. Our online community on HealthUnlocked brings patients, their loved-ones and healthcare experts all together online to share advice and encouragement.`}
          {' '}
          <a
            className="navigator__footer__link"
            href="https://healthunlocked.com/bhf"
            rel="noreferrer"
            target="_blank"
          >
            Join the conversation {'>'}
          </a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Lower your cholesterol | HealthUnlocked',
};

export default obj;
